/**
 * Detect whether the embed script is running inside a site/page builder
 * or site editor rather than on a page served to a real visitor.
 *
 * The customer pastes the loader into their page content, so it also
 * executes while they edit that page (Divi Visual Builder, the WordPress
 * block editor, etc.). A floating chat launcher there is just noise in
 * the editing canvas and can overlap builder chrome, so the loader bails
 * when this returns true.
 *
 * Detection is best-effort and fail-safe — any unexpected error returns
 * false so a normal page still renders the launcher.
 */
export function inEditorContext(): boolean {
  try {
    const w = window as unknown as Record<string, unknown>;
    const search = window.location.search || "";
    // Builder "edit/preview" URL flags: Divi (et_fb), Beaver (fl_builder),
    // Elementor, WPBakery (vc_editable), Brizy, Oxygen (ct_builder).
    if (
      /[?&](et_fb|et_pb_preview|fl_builder|elementor-preview|vc_editable|brizy-edit|ct_builder)=/.test(
        search,
      )
    ) {
      return true;
    }
    // WordPress admin (classic/block editor, Divi backend builder).
    if (/\/wp-admin\//.test(window.location.pathname)) return true;

    const body = typeof document !== "undefined" ? document.body : null;
    const cls = body && body.classList;
    if (
      cls &&
      // NOTE: do NOT match ``et-db`` — Divi puts it on EVERY front-end page
      // (it marks Divi-built content), not just the builder, so it would
      // suppress the launcher site-wide. The front-end Visual Builder adds
      // ``et-fb``; the backend builder lives under ``/wp-admin/`` (caught
      // above).
      (cls.contains("et-fb") || // Divi front-end Visual Builder
        cls.contains("block-editor-page") || // WP block editor
        cls.contains("brz-ed") || // Brizy
        cls.contains("elementor-editor-active"))
    ) {
      return true;
    }
    // Builder globals present only while editing.
    if (w.ET_Builder || w.ETBuilderBackend || w.et_fb_options) return true;
  } catch {
    // Detection must never break the loader — default to rendering.
  }
  return false;
}
